import {
  Add,
  DoNotDisturb,
  Edit,
  Delete,
  SettingsBackupRestore,
  ViewList,
  History,
} from '@mui/icons-material';
import { Alert, Box, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import useDebounce from '../../../hooks/useDebounce';
import { useAccess } from '../../../hooks/useAccess';
import {
  deleteTestSuite,
  getTestSuitesWithMetrics,
  handleActivateText,
  handleSuspendText,
  suspendTestSuite,
} from '../../../modules/testsuites';
import { testSuiteTableHeader } from '../../../static-data/users';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModal';
import SearchBar from '../../../ui/primitives/SearchBar';
import TableComponent from '../../../ui/data/table/TableComponent';
import TestSuiteTableAction from './TestSuiteTableAction';
import ViewTestCaseModal from './ViewTestCaseModal';
import ActionButton from '../../../ui/primitives/ActionButton';
import ActionMenu from '../../../ui/primitives/ActionMenu';
import { Guard } from '../../../features/Guard';

const MODAL_ACTION = {
  STATUS_CHANGE: 'STATUS_CHANGE',
  PERMANENT_DELETE: 'PERMANENT_DELETE',
};

const TestSuitesTabContent = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { addNotification } = useNotification();

  const { canAccess } = useAccess();
  const canManage = canAccess('ManageTestSuite');
  const canDelete = canAccess('DeleteTestSuite');

  const [loading, setLoading] = useState(true);
  const [testSuites, setSuites] = useState([]);
  const [order, setOrder] = useState('asc');
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [modalAction, setModalAction] = useState(MODAL_ACTION.STATUS_CHANGE);
  const [openAdd, setOpenAdd] = useState(false);
  const [mode, setMode] = useState('add');
  const [viewTestCase, setViewTestCase] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchQuery = useDebounce(searchValue, 800);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const handleDeleteClick = (suite) => {
    setOpenDelete(true);
    setModalAction(MODAL_ACTION.STATUS_CHANGE);
    setSelectedSuite(suite);
  };

  const handlePermanentDeleteClick = (suite) => {
    setOpenDelete(true);
    setModalAction(MODAL_ACTION.PERMANENT_DELETE);
    setSelectedSuite(suite);
  };

  const handleEditClick = (suite) => {
    setOpenAdd(true);
    setSelectedSuite(suite);
    setMode('edit');
  };

  const handleDeleteCancel = () => {
    setOpenDelete(false);
    setModalAction(MODAL_ACTION.STATUS_CHANGE);
    setSelectedSuite(null);
  };

  const handleViewClick = (suite) => {
    setViewTestCase(true);
    setSelectedSuite(suite);
  };

  const handleViewClose = () => {
    setViewTestCase(false);
    setSelectedSuite(null);
  };

  const handleSearch = (value) => {
    setLoading(true);
    setSearchValue(value);
    setPage(1);
  };

  const fetchTestSuitesData = async () => {
    setLoading(true);
    try {
      const response = await getTestSuitesWithMetrics(
        projectId,
        page,
        perPage,
        searchValue
      );
      setSuites(response?.data || []);
      setTotalCount(response?.total || 0);
    } catch (error) {
      addNotification(
        'error',
        error?.message || 'Failed to fetch test suites.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestSuitesData();
  }, [projectId, debouncedSearchQuery, page, perPage]);

  const handleDelete = async () => {
    try {
      if (modalAction === MODAL_ACTION.PERMANENT_DELETE) {
        if (selectedSuite?.test_case_count > 0) {
          throw new Error(
            "This suite can't be deleted as it contains test cases."
          );
        }
        await deleteTestSuite(selectedSuite.id);

        addNotification(
          'success',
          `Test suite "${selectedSuite.name}" permanently deleted.`
        );
      } else {
        const status = selectedSuite?.status === 'suspended' ? 'active' : 'suspended';
        await suspendTestSuite({ status, id: selectedSuite.id });
        addNotification(
          'success',
          `Test suite successfully ${selectedSuite?.status === 'suspended'
            ? 'activated'
            : 'marked as suspended'
          }.`
        );
      }
      fetchTestSuitesData();
      handleDeleteCancel();
    } catch (error) {
      addNotification(
        'error',
        error?.message || 'Failed to update test suite. Please try again.'
      );
      handleDeleteCancel();
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  const getRowMenuItems = (row) => {
    const isSuspended = row.status === 'suspended';

    return [
      {
        label: 'Manage Test Cases',
        icon: <ViewList />,
        onClick: () => handleViewClick(row),
      },
      {
        label: 'View History',
        icon: <History />,
        onClick: () => navigate(`/project/${projectId}/testsuite/${row.id}/history`),
      },
      {
        label: 'Edit Test Suite',
        icon: <Edit />,
        onClick: () => handleEditClick(row),
        hidden: !canManage || isSuspended,
      },
      {
        label: 'Suspend Test Suite',
        icon: <DoNotDisturb />,
        color: 'warning',
        onClick: () => handleDeleteClick(row),
        hidden: !canManage || isSuspended,
      },
      {
        label: 'Restore Test Suite',
        icon: <SettingsBackupRestore />,
        color: 'success',
        onClick: () => handleDeleteClick(row),
        hidden: !canManage || !isSuspended,
      },
      {
        label: 'Permanently Delete',
        icon: <Delete />,
        color: 'error',
        onClick: () => handlePermanentDeleteClick(row),
        hidden: !canDelete || !isSuspended,
      },
    ];
  };

  return (
    <>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Test Suites
      </Typography>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}>
        <SearchBar
          placeholder="Search test suite(s)"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          clearSearch={() => handleSearch('')}
        />
        <Guard policy="ManageTestSuite">
          <ActionButton
            intent="primary"
            icon={<Add />}
            label="New Test Suite"
            size="large"
            onClick={() => {
              setSelectedSuite(null);
              setMode('add');
              setOpenAdd(true);
            }}
          />
        </Guard>
      </Stack>

      {!loading && searchValue === '' && testSuites?.length === 0 && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Alert severity="warning">
            {canManage
              ? 'No test suites found. Please create a test suite to add test cases, as they are required for adding test cases.'
              : 'No test suites found. Please ask your project admin to create test suites, as they are required for adding test cases.'}
          </Alert>
        </Box>
      )}

      <TableComponent
        data={testSuites || []}
        headerTabs={testSuiteTableHeader}
        page={page}
        setPage={setPage}
        perPage={perPage}
        order={order}
        setOrder={setOrder}
        defaultSort="latest_execution_date"
        isLoading={loading}
        striped
        totalCount={totalCount}
        handlePageChange={handlePageChange}
        showPages
        tablekey="test suites"
        showCount
        showRowCount
        setPerPage={handlePageSizeChange}
        action={(row) => (
          <ActionMenu
            items={getRowMenuItems(row)}
            tooltip="Suite actions"
          />
        )}
      />

      <ConfirmationModal
        show={openDelete}
        handleConfirm={handleDelete}
        handleClose={handleDeleteCancel}
        title={
          modalAction === MODAL_ACTION.PERMANENT_DELETE
            ? 'Permanently Delete Test Suite'
            : selectedSuite?.status === 'suspended'
              ? 'Restore Test Suite'
              : 'Suspend Test Suite'
        }
        body={
          modalAction === MODAL_ACTION.PERMANENT_DELETE
            ? `Are you sure you want to permanently delete the test suite: ${selectedSuite?.name}? This action is irreversible.`
            : selectedSuite?.status === 'suspended'
              ? handleActivateText
              : handleSuspendText
        }
        confirmButtonVariant={
          modalAction === MODAL_ACTION.PERMANENT_DELETE ? 'danger' : 'primary'
        }
      />

      <TestSuiteTableAction
        fetchTestSuitesData={fetchTestSuitesData}
        setOpenModal={setOpenAdd}
        open={openAdd}
        mode={mode}
        setMode={setMode}
        selectedSuite={selectedSuite}
        setSelectedSuite={setSelectedSuite}
      />

      <ViewTestCaseModal
        show={viewTestCase}
        handleClose={handleViewClose}
        selectedSuite={selectedSuite}
        fetchTestSuitesData={fetchTestSuitesData}
      />
    </>
  );
};

export default TestSuitesTabContent;